import { Base64 } from "js-base64";
import { cart_cart, cart_cart_subcarts, cart_cart_subcarts_lines } from "@src/pages/Cart/__generated__/cart";
import { variantForCart } from "@src/pages/Cart/__generated__/variantForCart";
import { checkout } from "@components/pages/CheckoutPage/__generated__/checkout";
import {
  checkoutUpdate_checkoutUpdate_checkout_subcarts,
  checkoutUpdate_checkoutUpdate_checkout_subcarts_lines
} from "@components/pages/CheckoutPage/__generated__/checkoutUpdate";
import {
  productThumbnail, productThumbnail_price,
  productThumbnail_variants
} from "@components/ProductPage/ProductThumbnail/__generated__/productThumbnail";
import {
  CheckoutError,
  CheckoutInfoWarning,
  GAItem,
  GAItemForCart,
  GAItemForItemList,
  GAItemForOrder
} from "@src/CommonTypes";
import { cartBadgeType } from "@providers";
import {
  product_product,
  product_product_variants
} from "@components/ProductPage/ProductDetailPage/__generated__/product";
import { useEffect, useState } from "react";
import GA4React from "ga-4-react";
import { GA4Config, GA4ReactResolveInterface } from "ga-4-react/src/models/gtagModels";
import { products_products, productsVariables, products_products_edges } from "@components/ProductPage/ProductList/__generated__/products";
import { mobileProducts_products, mobileProducts_products_edges } from "@src/components/ProductPage/ProductList/__generated__/mobileProducts";
import { availability } from "@components/ProductPage/ProductThumbnail/__generated__/availability";
import { format } from "date-fns";
import { orderForGaPurchase } from "@src/queries/__generated__/orderForGaPurchase";
import { formatPrice } from "@src/utils";
// import { each } from "lodash";
// import { FetchResult } from "react-apoll";

// import { OrderDirection, ProductOrderField } from "../../types/globalTypes";
// import { IFilterAttributes } from "../@next/types";
// import { FormError } from "./types";

export const joinErrorMessages = (errors: { message: string }[]) => {
  const errArr = errors.map((err) => {
    return err.message;
  });
  return errArr.join("\n");
};

export const getCartBadgeFromCart = (cart: any): cartBadgeType => {
  return {
    __typename: "Cart",
    token: cart?.token,
    quantity: cart?.quantity,
    forMgs: cart?.forMgs,
  };
};

export const mergeMessagesAndErrors = (
  messages: CheckoutInfoWarning[] = [],
  errors: CheckoutError[] = []
) => {
  if (!messages || !errors) {
    messages = [];
    errors = [];
  }
  const errorMessages =
    errors?.map((msg) => {
      return { level: "danger", message: msg.message };
    }) || [];
  return [...messages, ...errorMessages];
};

export const getPriceInfoFromAvailability = (availability: availability, priceAmount: number) => {
  const originalPrice = priceAmount;
  const finalPrice = availability?.priceRange?.start?.gross?.amount;
  const discountAmount = availability?.priceRange?.start.gross?.amount ?? 0;
  const discountPercent = Math.round(100 * (priceAmount - availability!.priceRange!.start!.gross.amount) / priceAmount);
  const isOnSale = availability?.onSale && priceAmount !== finalPrice;
  const isOutOfStock = availability?.isOutOfStock;
  return {
    originalPrice,
    finalPrice,
    discountAmount,
    discountPercent,
    isOnSale,
    isOutOfStock,
  };
};

export const getMaskedEmail = (email: string) => {
  const userName = email.split("@");
  const parsingName = userName[0].slice(0, 2);

  const maskedEmail = `${parsingName}***`
  return maskedEmail;
};

export const iterateCartLines = <
  T extends
  | cart_cart_subcarts_lines
  | checkoutUpdate_checkoutUpdate_checkout_subcarts_lines
>(
  cart: cart_cart | checkout
) => {
  let freebies: (T | null)[] = [];
  let notFreebies: (T | null)[][] = [];

  if (cart?.subcarts) {
    (cart?.subcarts as Array<
      cart_cart_subcarts | checkoutUpdate_checkoutUpdate_checkout_subcarts
    >).map((subcart, i) => {
      notFreebies.push([]);
      (subcart.lines as Array<T>).map((line) => {
        if (line.isFreebie) {
          freebies.push(line);
        } else {
          notFreebies[i].push(line);
        }
      });
    });
  }
  return {
    freebies,
    notFreebies,
  };
};

export const slugify = (text: string | number): string =>
  text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/&/g, "-and-") // Replace & with 'and'
    .replace(/[^\w\-]+/g, "") // Remove all non-word chars
    .replace(/\-\-+/g, "-"); // Replace multiple - with single -

export const getDBIdFromGraphqlId = (
  graphqlId: string,
  schema?: string
): number => {
  // This is temporary solution, we will use slugs in the future
  const rawId = Base64.decode(graphqlId);
  const regexp = /(\w+):(\d+)/;
  const arr = regexp.exec(rawId);
  if (schema && schema !== arr![1]) {
    throw new Error("Schema is not correct");
  }
  return parseInt(arr![2], 10);
};

export const getGraphqlIdFromDBId = (id: string, schema: string): string =>
  // This is temporary solution, we will use slugs in the future
  Base64.encode(`${schema}:${id}`);

export const getMstiTypeFromUrl = (
  mstiType: string,
  acneType?: string
): string => {
  return mstiType;
  // + acneType;
};

export const priceToString = (
  price: { amount: number; currency: string },
  locale?: string
): string => {
  const { amount } = price;
  if (locale) {
    return amount.toLocaleString(locale, {
      currency: price.currency,
      style: "currency",
    });
  } else {
    return `${price.currency} ${amount.toFixed(2)}`;
  }
};

// export const generateProductUrl = (id: string, name: string) =>
//   `/product/${slugify(name)}/${getDBIdFromGraphqlId(id, "Product")}/`;
//
// export const generateCategoryUrl = (id: string, name: string) =>
//   `/category/${slugify(name)}/${getDBIdFromGraphqlId(id, "Category")}/`;
//
// export const generateCollectionUrl = (id: string, name: string) =>
//   `/collection/${slugify(name)}/${getDBIdFromGraphqlId(id, "Collection")}/`;

export const generatePageUrl = (slug: string) => `/page/${slug}/`;

export const maccaronDateForm = (date: string, formatString?: string) => {
  const dt = Date.parse(date);
  // Date.parse returns NaN if parsing failed. !!NaN is equal to false
  return !!dt ? format(dt, formatString ? formatString : "dd MMM yy") : "";
};

interface AttributeDict {
  [attributeSlug: string]: string[];
}

// export const convertToAttributeScalar = (
//   attributes: AttributeDict | IFilterAttributes
// ) =>
//   Object.entries(attributes)
//     .map(([key, value]) =>
//       value.map((attribute: any) => ({ slug: key, value: attribute }))
//     )
//     .reduce((prev, curr) => [...prev, ...curr], []);

// interface QueryString {
//   [key: string]: string[] | string | null | undefined;
// }
// export const getAttributesFromQs = (qs: QueryString) =>
//   Object.keys(qs)
//     .filter(
//       key => !["pageSize", "priceGte", "priceLte", "sortBy", "q"].includes(key)
//     )
//     .reduce((prev: any, curr: any) => {
//       prev[curr] = typeof qs[curr] === "string" ? [qs[curr]] : qs[curr];
//       return prev;
//     }, {});

export const getValueOrEmpty = <T>(value: T): T | string =>
  value === undefined || value === null ? "" : value;

// export const convertSortByFromString = (sortBy: string) => {
//   if (!sortBy) {
//     return null;
//   }
//   const direction = sortBy.startsWith("-")
//     ? OrderDirection.DESC
//     : OrderDirection.ASC;

//   let field;
//   switch (sortBy.replace(/^-/, "")) {
//     case "name":
//       field = ProductOrderField.NAME;
//       break;

//     case "price":
//       field = ProductOrderField.MINIMAL_PRICE;
//       break;

//     case "updated_at":
//       field = ProductOrderField.DATE;
//       break;

//     default:
//       return null;
//   }
//   return { field, direction };
// };

export const maybe = <T>(exp: () => T, d?: T) => {
  try {
    const result = exp();
    return result === undefined ? d : result;
  } catch {
    return d;
  }
};

// export const parseQueryString = (
//   location: LocationState
// ): { [key: string]: string } => {
//   const query = {
//     ...parseQs((location as any).search.substr(1)),
//   };
//   each(query, (value, key) => {
//     if (Array.isArray(value)) {
//       query[key] = value[0];
//     }
//   });
//   return query as { [key: string]: string };
// };

// export const updateQueryString = (
//   location: LocationState,
//   history: History
// ) => {
//   const querystring = parseQueryString(location);

//   return (key: string, value?: any) => {
//     if (value === "") {
//       delete querystring[key];
//     } else {
//       querystring[key] = value || key;
//     }
//     history.replace("?" + stringifyQs(querystring));
//   };
// };

// export const findFormErrors = (result: void | FetchResult): FormError[] => {
//   if (result) {
//     const data = Object.values(maybe(() => result.data) as object);

//     return data.reduce((prevVal: any, currVal: any) => {
//       const errors = currVal.errors || [];

//       return [...prevVal, ...errors];
//     }, []);
//   }
//   return [];
// };

// export const removeEmptySpaces = (text: string) => text.replace(/\s+/g, "");

export function getIsMobile() {
  let check = false;
  (function (a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
}

export const starRatingSvg =
  "M34.753,11.663l-9.9-1.448L20.417,1.183a2.382,2.382,0,0,0-4.117,0l-4.429,9.032L1.966,11.663A2.317,2.317,0,0,0,.694,15.606l7.169,7.03L6.172,32.564A2.3,2.3,0,0,0,9.5,35l8.858-4.686L27.219,35a2.3,2.3,0,0,0,3.33-2.434l-1.691-9.928,7.169-7.03A2.32,2.32,0,0,0,34.753,11.663Z";

export function getPriceWithFraction(
  amount: number,
  unit: boolean,
  fraction: boolean,
  minimumFractionDigits: number = 2
) {
  let result = "";
  if (unit) {
    result += "₹";
  }
  if (fraction) {
    result += amount?.toLocaleString("en-US", {
      maximumFractionDigits: 2,
      minimumFractionDigits: minimumFractionDigits,
    });
  } else {
    result += amount?.toLocaleString("en-US");
  }
  return result;
}

export function getPrice(amount: number, withUnit: boolean) {
  if (!amount) return 0;
  var result = "";
  if (withUnit) {
    result = "₹" + result;
  }
  var num_parts = amount.toString().split(".");
  num_parts[0] = num_parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  result = result + num_parts.join(".");
  return result;
}

export function getDateTime(timestamp: any) {
  return new Date(Date.parse(timestamp)).toLocaleString();
}

export function shuffle(array: any[]) {
  var currentIndex = array.length,
    temporaryValue,
    randomIndex;

  // While there remain elements to shuffle...
  while (0 !== currentIndex) {
    // Pick a remaining element...
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;

    // And swap it with the current element.
    temporaryValue = array[currentIndex];
    array[currentIndex] = array[randomIndex];
    array[randomIndex] = temporaryValue;
  }
  return array;
}

export function useOutsideHandler(ref: any, callback: any) {
  useEffect(() => {
    function handleClickOutside(event: any) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);
}

export const processVariant = (
  variant: variantForCart,
  productNameBackup?: string
) => {
  const product = variant?.product;
  const showVariants =
    product?.productType?.hasVariants && 1 < (product?.variants?.length ?? 0);
  let variantImageUrl = "";
  let variantImageAlt = "";
  if (showVariants) {
    const img = variant?.images?.sort((a, b) => a.sortOrder - b.sortOrder)[0];
    variantImageUrl = img?.url;
    variantImageAlt = img?.alt;
  } else {
    const img = product?.thumbnail;
    variantImageUrl = img?.url;
    variantImageAlt = img?.alt;
  }
  const attributeValue = variant?.attributes[0]?.value;
  const attributeImage = attributeValue?.image;
  const attributeImageUrl = attributeImage?.url;
  const attributeImageAlt = attributeImage?.alt;
  const isOnSale = product?.availability?.onSale;
  const variantName = attributeValue?.name;
  const productBrand = product?.brand;
  const productName = product?.name ?? productNameBackup;
  const isMgs = product?.productType?.isMgs;
  return {
    variantImageUrl,
    variantImageAlt,
    attributeImageUrl,
    attributeImageAlt,
    showVariants,
    isOnSale,
    variantName,
    productBrand,
    productName,
    isMgs,
  };
};

export const isNotEmpty = (obj) => {
  return 0 < Object.keys(obj).length;
};

export const useContainerDimensions = (myRef) => {
  const getDimensions = () => ({
    width: myRef.current.offsetWidth,
    height: myRef.current.offsetHeight,
  });

  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const handleResize = () => {
      setDimensions(getDimensions());
    };

    if (myRef.current) {
      setDimensions(getDimensions());
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [myRef]);

  return dimensions;
};

export const getPriceFromVariants = (
  showVariants: boolean,
  sortedVariants: (productThumbnail_variants | product_product_variants)[],
  selectedVariant: number | null,
  product: product_product | productThumbnail
) => {
  let currentVariant,
    finalPrice,
    originalPrice,
    isOnSale,
    discountAmount,
    showAddToCartButton;

  if (showVariants && selectedVariant !== null) {
    currentVariant = sortedVariants[selectedVariant];
    finalPrice = currentVariant?.discountedPrice?.amount;
    originalPrice = currentVariant?.price?.amount; // overridden price of variant
    discountAmount = finalPrice;
    isOnSale = 0 < discountAmount;
    showAddToCartButton = 0 < (currentVariant?.stockQuantity ?? 0);
  } else {
    const availability = product?.availability;
    currentVariant = sortedVariants[0]; // need to set for notifyme
    finalPrice = availability?.priceRange?.start?.gross?.amount;
    originalPrice = product?.price?.amount;
    discountAmount = availability?.priceRange?.start?.gross?.amount ?? 0;
    isOnSale = availability?.onSale ?? false;
    showAddToCartButton = !availability?.isOutOfStock;
  }
  return {
    currentVariant,
    finalPrice,
    originalPrice,
    isOnSale,
    discountAmount,
    showAddToCartButton,
  };
};

export const isValidEmail = (email) => {
  // https://stackoverflow.com/a/46181
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const gaV4GetItemsFromCart = (
  cart: cart_cart | checkout
): GAItemForCart[] => {
  const items: GAItemForCart[] = [];
  cart?.subcarts?.forEach((s, i) => {
    s.lines.forEach((l, j) => {
      const v = l?.variant;
      const p = v?.product;
      const variantId = getDBIdFromGraphqlId(v?.id).toString();
      const productId = getDBIdFromGraphqlId(p?.id).toString();
      const productName = p?.name;
      const discount = p?.availability?.discount?.gross?.amount;
      items.push({
        item_id: productId,
        item_name: productName,
        variant_id: variantId,
        quantity: l?.quantity,
        price: v?.price?.amount,
        currency: "INR",
        discount: discount,
      });
    });
  });
  return items;
};

export const gaV4GetItemsFromOrder = (
  order: orderForGaPurchase
): GAItemForOrder[] => {
  const items: GAItemForOrder[] = [];
  order.subs.forEach((s, i) => {
    s.lines.forEach((l, j) => {
      const variant = l?.variant;
      const product = variant?.product;
      const variantId = getDBIdFromGraphqlId(variant?.id).toString();
      const productId = getDBIdFromGraphqlId(product?.id).toString();
      const productName = product?.name;
      const discount = product?.availability?.discount?.gross?.amount;
      const tax = l?.unitPrice?.tax?.amount;
      items.push({
        item_id: productId,
        item_name: productName,
        variant_id: variantId,
        quantity: l.quantity,
        price: variant?.price?.amount,
        currency: "INR",
        discount: discount,
        tax: tax,
      });
    });
  });
  return items;
};

export const gaV4GetItemsFromProducts = (
  products: products_products | mobileProducts_products,
  itemListId: string
): GAItemForItemList[] => {
  const productEdges: (products_products_edges | mobileProducts_products_edges)[] = products?.edges;
  return productEdges?.map((e, i) => {
    const p = e?.node;
    const itemId = getDBIdFromGraphqlId(p?.id).toString();
    const productName = p?.name;
    const discount = p?.availability?.priceRange?.start?.gross?.amount;
    return {
      item_id: itemId,
      item_name: productName,
      quantity: 1,
      price: p?.price?.amount ?? 0,
      currency: "INR",
      item_list_id: itemListId,
      index: i,
      discount: discount,
    };
  });
};

export const gaV4GetItemsFromSingleProduct = (p: product_product): GAItem[] => {
  const itemId = getDBIdFromGraphqlId(p?.id).toString();
  const productName = p?.name;
  const discount = p?.availability?.priceRange?.start?.gross?.amount;
  return [
    {
      item_id: itemId,
      item_name: productName,
      price: p?.price?.amount,
      currency: "INR",
      quantity: 1,
      discount: discount,
    },
  ];
};

export const gaV4GetItemListIdFromInitialVariables = (
  initialVariables: productsVariables
): string | null => {
  if (0 < (initialVariables?.collections?.length ?? 0)) {
    return Base64.decode(initialVariables?.collections[0]);
  } else if (0 < (initialVariables?.collections?.length ?? 0)) {
    return Base64.decode(initialVariables?.categories[0]);
  } else if (initialVariables?.query) {
    return `Search:${initialVariables?.query}`;
  } else if (initialVariables?.tag) {
    return Base64.decode(initialVariables?.tag);
  } else {
    return null;
  }
};

export const getGaConfig = (): GA4Config => {
  const gaConfig = {
    send_page_view: true,
  };
  // don't use nullable operator (?) in process.env
  // since it will be run on runtime to which typescript is not applied
  if (process.env.GA_V4_DEBUG_MODE === "true") {
    gaConfig["debug_mode"] = true;
  }
  const userId = getUserId();
  if (userId) {
    gaConfig["user_id"] = userId;
  }
  return gaConfig;
};

export const getOrCreateGA4React = async (
  gaCode?: string
): Promise<GA4ReactResolveInterface | null> => {
  try {
    if (GA4React.isInitialized()) {
      return GA4React.getGA4React() as GA4ReactResolveInterface;
    } else {
      const gaConfig = getGaConfig();
      const script = new GA4React(gaCode, gaConfig);
      return await script.initialize(); // it must be awaited.
    }
  } catch (e) {
    return null;
  }
};

export const gaV4AddToWishList = async (
  previousPage: string,
  wishlistProductId: number,
  wishlistProductName: string,
  wishlistProductPrice: number
) => {
  const ga4react = await getOrCreateGA4React();
  ga4react?.gtag("event", "add_to_wishlist", {
    previous_page: previousPage,
    items: [
      {
        item_id: wishlistProductId.toString(),
        item_name: wishlistProductName.toString(),
        quantity: 1,
        price: wishlistProductPrice,
        currency: "INR",
      },
    ],
    value: wishlistProductPrice,
  });
};

export const gaV4AddToCart = async (
  previousPage: string,
  productId: number,
  productName: string,
  variantId: number,
  variantPrice: number,
  discount: number
): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  const item: GAItemForCart = {
    item_id: productId.toString(),
    item_name: productName,
    variant_id: variantId.toString(),
    quantity: 1,
    price: variantPrice,
    currency: "INR",
    discount: discount,
  };
  ga4react?.gtag("event", "add_to_cart", {
    previous_page: previousPage,
    items: [item],
    value: variantPrice,
  });
};

export const gaV4ViewCart = async (
  items: GAItemForCart[],
  value: number
): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  ga4react?.gtag("event", "view_cart", {
    items: items,
    value: value,
  });
};

export const gaV4BeginCheckout = async (
  items: GAItemForCart[],
  value: number
): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  ga4react?.gtag("event", "begin_checkout", {
    items: items,
    value: value,
  });
};

export const gaV4BeginPayment = async (
  items: GAItemForOrder[],
  value: number,
  usedPoint: number,
  discount: number,
  coupon: string
): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  ga4react?.gtag("event", "begin_payment", {
    items: items,
    value: value,
    used_point: usedPoint,
    discount: discount,
    coupon: coupon,
  });
};

export const gaV4Purchase = async (
  order: orderForGaPurchase
): Promise<void> => {
  // product.discount: reflect brand discount
  // order.discount: reflects voucher discount
  const ga4react = await getOrCreateGA4React();
  const items = gaV4GetItemsFromOrder(order);
  const value = order?.totalCaptured?.amount ?? 0;
  const usedPoint = order?.usedPoint?.amount ?? 0;
  const discount = order?.discountAmount?.amount ?? 0;
  const coupon = order?.voucher?.name ?? "";
  const transactionId = getDBIdFromGraphqlId(order?.id).toString();
  const shipping = order?.shippingPrice?.gross?.amount;
  const tax = order?.total?.tax?.amount;
  const paymentType = order?.paymentType;
  ga4react?.gtag("event", "purchase", {
    items: items,
    value: value,
    used_point: usedPoint,
    discount: discount,
    coupon: coupon,
    transaction_id: transactionId,
    shipping: shipping,
    tax: tax,
    payment_type: paymentType,
    currency: "INR",
  });
};

export const gaV4ViewItemList = async (
  items: GAItemForItemList[],
  itemListId: string
): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  ga4react?.gtag("event", "view_item_list", {
    items: items,
    item_list_id: itemListId,
  });
};

export const gaV4ViewItem = async (
  items: GAItem[],
  value: number
): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  const d = new Date();
  const datetime = `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
  ga4react?.gtag("event", "view_item", {
    items: items,
    value: value,
    datetime: datetime,
  });
};

export const gaV4ClickNotifyMe = async (
  productId: string,
  variantId: string,
  variantPrice: number
): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  ga4react?.gtag("event", "click_notifyme", {
    item_id: productId,
    variant_id: variantId,
    quantity: 1,
    currency: "INR",
    items: [
      {
        item_id: productId,
        variant_id: variantId,
        quantity: 1,
        price: variantPrice,
        currency: "INR",
      },
    ],
    value: variantPrice,
  });
};

export const getUserId = (): string | null => {
  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  if (!currentUser) {
    return 'anonymous';
  }
  return getDBIdFromGraphqlId(currentUser?.user?.id)?.toString();
};

export const gaV4SetUserId = async (): Promise<void> => {
  const userId = getUserId();
  const gaMeasurementId = process.env.GA_V4_TRACKING_ID;
  if (!gaMeasurementId) {
    return;
  }
  const ga4react = await getOrCreateGA4React();
  ga4react?.gtag("config", gaMeasurementId, {
    user_id: userId,
  });
};

export const gaV4BannerExposed = async (bannerName: string): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  ga4react?.gtag("event", "banner_exposed", {
    banner_name: bannerName,
  });
};

export const gaV4ClickBanner = async (bannerName: string): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  ga4react?.gtag("event", "click_banner", {
    banner_name: bannerName,
  });
};

export const gaV4ClickOffer = async (offerName: string): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  ga4react?.gtag("event", "offer_click", {
    name: offerName,
  });
}

export const gaV4RemoveFromWishList = async (
  previousPage: string,
  wishlistProductId: number,
  wishlistProductPrice: number
): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  const userId = getUserId();
  ga4react?.gtag("event", "remove_from_wishlist", {
    user_id: userId,
    value: wishlistProductPrice,
    previous_page: previousPage,
    items: [
      {
        item_id: wishlistProductId.toString(),
        quantity: 1,
        price: wishlistProductPrice,
        currency: "INR",
      },
    ],
  });
};

export const gaV4Login = async (
  method: string,
  userId: string
): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  ga4react?.gtag("event", "login", {
    method: method,
    user_id: userId, // use parameter instead of getUserId() due to async problem
  });
};

export const gaV4SignUp = async (
  method: string
): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  ga4react?.gtag("event", "sign_up", {
    method: method
  });
};

export const gaV4RecommendedProductExposed = async (
  sourceProductId: string,
  targetProductId: string,
  index: number,
  recommendationType: string
): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  ga4react?.gtag("event", "recommended_product_exposed", {
    source_product_id: sourceProductId,
    target_product_id: targetProductId,
    index: index,
    recommendation_type: recommendationType,
  });
};

export const gaV4ClickRecommendedProduct = async (
  sourceProductId: string,
  targetProductId: string,
  index: number,
  recommendationType: string
): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  ga4react?.gtag("event", "click_recommended_product", {
    source_product_id: sourceProductId,
    target_product_id: targetProductId,
    index: index,
    recommendation_type: recommendationType,
  });
};

export const gaV4ShareProduct = async (itemId: string): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  const userId = getUserId();
  ga4react?.gtag("event", "share_product", {
    item_id: itemId,
    user_id: userId,
  });
};

export const gaV4SearchStart = async (): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  const userId = getUserId();
  ga4react?.gtag("event", "search_start", {
    user_id: userId,
  });
};

export const gaV4SearchResultAction = async (
  actionType: string,
  keywordId: string,
  keyword: string,
  searchText: string
): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  const userId = getUserId();
  ga4react?.gtag("event", "search_result_action", {
    user_id: userId,
    action_type: actionType,
    keyword_id: keywordId,
    keyword: keyword,
    search_text: searchText,
  });
};

export const gaV4SearchProductDetail = async (
  productId: string,
  productName: string,
  brandId: string,
  brandName: string,
  keyword: string,
  searchText: string
): Promise<void> => {
  const ga4react = await getOrCreateGA4React();
  const userId = getUserId();
  ga4react?.gtag("event", "search_product_detail", {
    user_id: userId,
    product_id: productId,
    product_name: productName,
    brand_id: brandId,
    brand_name: brandName,
    keyword: keyword,
    search_text: searchText,
  });
};

export const getUrlSearchParams = (locationUrl: string, matchParam: string) => {
  const url = new URL(locationUrl);
  const urlParams = url.searchParams;
  if (urlParams.get(matchParam) == null) {
    return "";
  } else {
    return urlParams.get(matchParam).split("_");
  }
};

export const getSlugFromUrl = (locationUrl: string) => {
  const splitUrl = locationUrl.split('/');
  const popedItem = splitUrl.pop();
  if (popedItem != '') {
    return popedItem;
  } else {
    return splitUrl.pop();
  }
}

export const getSlugFromText = (text: string) => {
  return text.toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export const getHighestDiscountVoucher = (product) => {
  const maxVoucher = product.availableVouchers.reduce((maxVoucher, currentVoucher) => {
    if (currentVoucher.discountValue > (maxVoucher?.discountValue || 0)) {
      return currentVoucher; // 현재 바우처의 할인율이 더 높으면 반환
    }
    return maxVoucher; // 그렇지 않으면 이전의 maxVoucher 반환
  }, null);
  if (maxVoucher) {
    if (maxVoucher.discountValueType === 'PERCENTAGE') {
      return formatPrice((product.availability?.priceRange?.start?.gross?.amount ?? product.price.amount)
        * ((100 - maxVoucher.discountValue) / 100));
    } else {
      return formatPrice((product.availability?.priceRange?.start?.gross?.amount ?? product.price.amount)
        - maxVoucher.discountValue);
    }
  }
  return '';
};

// Base64, String(id), Number(id) 에 상관 없이 ID 추출 함수
export function normalizeToDBIds(
    ids: string[],
    typename: 'Product' | 'Brand'
): string[] {
  const graphqlIdRegex = /^[A-Za-z0-9+/=]+$/;

  return Array.from(
      new Set(
          ids.map((id) => {
            if (!isNaN(Number(id))) {
              return id;
            }
            if (graphqlIdRegex.test(id)) {
              try {
                const dbId = getDBIdFromGraphqlId(id, typename);
                return dbId.toString();
              } catch {
                return id;
              }
            }
            return id;
          }),
      ),
  );
}